/**
 * recording-host.ts
 *
 * `RecordingHost` manages the lifecycle of durable audio capture sessions
 * that persist raw PCM chunks (via `../storage/recordings.ts`) as they
 * arrive from an `AudioWorkletNode` running `capture-processor.ts`.
 *
 * Two capture modes are supported:
 *
 *  - **Free Record**: a simple continuous capture session. Start/stop are
 *    driven directly by the caller (e.g. a record button). Every PCM chunk
 *    emitted by the worklet is appended to the active session as it
 *    arrives.
 *
 *  - **Word Record**: a state machine intended for capturing short,
 *    isolated word/phoneme utterances. It sequences a countdown, then arms
 *    itself for a press-and-hold gesture (e.g. pointerdown/pointerup),
 *    capturing audio only while "held", and automatically creates a
 *    `RegionAnnotation` bounding the pressed interval using sample-accurate
 *    indexing derived from the session's running `totalSamples` counter.
 *
 * This module is DOM-adjacent (used from the main thread) and owns the
 * `AudioWorkletNode` that taps `AudioGraph.source`. It does not create or
 * own the `AudioContext`/`MediaStream`; callers wire it into an existing
 * `AudioGraph`.
 */
import { appendChunk, createSession, deleteSession, getFullAudio, saveEnvelopePyramid, updateSessionStatus, upsertRegionAnnotation, } from "../storage/recordings.js";
import { buildEnvelopePyramid } from "../ui/waveform/envelope-pyramid.js";
const WORKLET_MODULE_URL = new URL("./capture-processor.js", import.meta.url).toString();
const DEFAULT_CHUNK_SIZE = 48000;
let workletModulePromise = null;
async function ensureWorkletRegistered(ctx) {
    if (!workletModulePromise) {
        workletModulePromise = ctx.audioWorklet
            .addModule(WORKLET_MODULE_URL)
            .catch((err) => {
            workletModulePromise = null;
            throw err;
        });
    }
    await workletModulePromise;
}
/**
 * Manages a live capture pipeline: worklet node -> chunk ingestion ->
 * IndexedDB persistence, plus the Free Record / Word Record state machines.
 */
export class RecordingHost {
    constructor(ctx, source, events = {}) {
        Object.defineProperty(this, "ctx", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "source", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "events", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "node", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "session", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "chunkIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "mode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "idle"
        });
        Object.defineProperty(this, "paused", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** Chunk appends still in flight (awaited before a session is finalized). */
        Object.defineProperty(this, "inflight", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "flushWaiters", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        // Word Record state.
        Object.defineProperty(this, "wordState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "idle"
        });
        Object.defineProperty(this, "wordOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "countdownTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "countdownDeadline", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "pressStartSample", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "pressStartMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        this.ctx = ctx;
        this.source = source;
        this.events = events;
    }
    /** Current top-level capture mode ("idle" | "free" | "word"). */
    getMode() {
        return this.mode;
    }
    /** The currently active session, if any. */
    getSession() {
        return this.session;
    }
    getWordState() {
        return this.wordState;
    }
    /** Whether a free recording is currently paused (tape open, not writing). */
    isPaused() {
        return this.paused;
    }
    // ---------------------------------------------------------------------
    // Worklet plumbing
    // ---------------------------------------------------------------------
    async createNode() {
        await ensureWorkletRegistered(this.ctx);
        const node = new AudioWorkletNode(this.ctx, "voice-pcm-recorder", {
            numberOfInputs: 1,
            numberOfOutputs: 0,
            channelCount: 1,
            processorOptions: { chunkSize: DEFAULT_CHUNK_SIZE },
        });
        node.port.onmessage = (e) => this.handleWorkletMessage(e.data);
        node.onprocessorerror = (e) => {
            console.error("[recording-host] worklet processor error", e);
            this.events.onError?.(e);
        };
        this.source.connect(node);
        return node;
    }
    disposeNode() {
        const node = this.node;
        if (!node)
            return;
        this.node = null;
        try {
            this.source.disconnect(node);
        }
        catch {
            /* already detached (paused) */
        }
        try {
            node.disconnect();
        }
        catch {
            /* best-effort */
        }
        node.port.onmessage = null;
    }
    handleWorkletMessage(data) {
        if (!data || typeof data !== "object")
            return;
        const msg = data;
        if (msg.type === "flushed") {
            const waiters = this.flushWaiters.splice(0);
            for (const w of waiters)
                w();
            return;
        }
        if (msg.type !== "pcm-chunk" || !msg.samples || !this.session)
            return;
        const pcm = new Int16Array(msg.samples);
        const idx = this.chunkIndex++;
        const p = appendChunk(this.session.id, idx, pcm)
            .then(() => {
            if (!this.session)
                return;
            this.session.totalSamples += pcm.length;
            this.events.onChunk?.(pcm.length, this.session.totalSamples);
        })
            .catch((err) => {
            console.error("[recording-host] failed to append chunk", err);
            this.events.onError?.(err);
        })
            .finally(() => {
            this.inflight.delete(p);
        });
        this.inflight.add(p);
    }
    /**
     * Ask the worklet to emit its partial chunk so everything captured so
     * far lands on the tape. Resolves on the worklet's acknowledgement (or a
     * short timeout so a wedged worklet cannot hang teardown).
     */
    flush(timeoutMs = 400) {
        const node = this.node;
        if (!node)
            return Promise.resolve();
        return new Promise((resolve) => {
            const timer = setTimeout(resolve, timeoutMs);
            this.flushWaiters.push(() => {
                clearTimeout(timer);
                resolve();
            });
            node.port.postMessage({ type: "flush" });
        });
    }
    /** Wait for every in-flight chunk append to settle. */
    async settle() {
        if (this.inflight.size === 0)
            return;
        await Promise.all([...this.inflight]);
    }
    // ---------------------------------------------------------------------
    // Free Record
    // ---------------------------------------------------------------------
    /** Begin a continuous, caller-controlled recording session. */
    async startFreeRecording(id) {
        if (this.mode !== "idle") {
            throw new Error(`[recording-host] cannot start free record in mode '${this.mode}'`);
        }
        this.mode = "free";
        this.chunkIndex = 0;
        this.paused = false;
        this.session = await createSession({ id, sampleRate: this.ctx.sampleRate });
        this.events.onSessionChange?.(this.session);
        this.node = await this.createNode();
        console.info(`[recording-host] free recording started '${id}'`);
        return this.session;
    }
    /** Stop the active free-recording session and finalize it. */
    async stopFreeRecording() {
        if (this.mode !== "free")
            return null;
        await this.flush();
        await this.settle();
        this.disposeNode();
        this.paused = false;
        if (this.session) {
            await updateSessionStatus(this.session.id, "stopped");
            this.session.status = "stopped";
            this.events.onSessionChange?.(this.session);
            // Build the envelope pyramid in batch, after the full recording
            // is available, rather than incrementally per-chunk. This keeps
            // the hot chunk-append path (invoked on every worklet message)
            // free of pyramid-building overhead, at the cost of a one-time
            // pass over the full PCM buffer when the session stops.
            try {
                const fullPcm = await getFullAudio(this.session.id);
                const pyramid = buildEnvelopePyramid(this.session.id, fullPcm);
                await saveEnvelopePyramid(pyramid);
            }
            catch (err) {
                console.error("[recording-host] failed to build/save envelope pyramid", err);
                this.events.onError?.(err);
            }
        }
        const session = this.session;
        this.session = null;
        this.mode = "idle";
        console.info("[recording-host] free recording stopped");
        return session;
    }
    /** Discard the active free-recording session entirely. */
    async cancelFreeRecording() {
        if (this.mode !== "free")
            return;
        this.disposeNode();
        this.paused = false;
        if (this.session) {
            await deleteSession(this.session.id);
        }
        this.session = null;
        this.mode = "idle";
    }
    /**
     * Pause a free recording: the worklet is detached from the source so no
     * samples accumulate, and the partial chunk is flushed so everything
     * captured so far is replayable. The session stays open and
     * `resumeFreeRecording()` continues contiguously — the paused interval
     * simply does not exist on the tape.
     */
    async pauseFreeRecording() {
        if (this.mode !== "free" || this.paused || !this.node)
            return;
        this.paused = true;
        try {
            this.source.disconnect(this.node);
        }
        catch (err) {
            console.warn("[recording-host] pause: disconnect failed", err);
        }
        await this.flush();
        await this.settle();
        console.info("[recording-host] free recording paused");
    }
    /** Resume a paused free recording; samples append contiguously. */
    resumeFreeRecording() {
        if (this.mode !== "free" || !this.paused || !this.node)
            return;
        this.source.connect(this.node);
        this.paused = false;
        console.info("[recording-host] free recording resumed");
    }
    // ---------------------------------------------------------------------
    // Word Record state machine
    // ---------------------------------------------------------------------
    /**
     * Begin a Word Record session: creates the session, wires the worklet,
     * and starts a countdown. After the countdown elapses the state
     * transitions to "armed", awaiting a press-and-hold gesture via
     * `onPressStart`/`onPressEnd`.
     */
    async startWordCapture(id, options) {
        if (this.mode !== "idle") {
            throw new Error(`[recording-host] cannot start word record in mode '${this.mode}'`);
        }
        this.mode = "word";
        this.wordOptions = options;
        this.chunkIndex = 0;
        this.pressStartSample = null;
        this.pressStartMs = null;
        this.session = await createSession({ id, sampleRate: this.ctx.sampleRate });
        this.events.onSessionChange?.(this.session);
        this.node = await this.createNode();
        this.setWordState("countdown");
        const countdownMs = options.countdownMs ?? 3000;
        this.runCountdown(countdownMs);
    }
    runCountdown(totalMs) {
        this.countdownDeadline = this.ctx.currentTime * 1000 + totalMs;
        const tick = () => {
            if (this.wordState !== "countdown")
                return;
            const remaining = this.countdownDeadline - this.ctx.currentTime * 1000;
            if (remaining <= 0) {
                this.events.onCountdownTick?.(0);
                this.setWordState("armed");
                return;
            }
            this.events.onCountdownTick?.(Math.max(0, Math.round(remaining)));
            this.countdownTimer = setTimeout(tick, 50);
        };
        tick();
    }
    setWordState(state) {
        this.wordState = state;
        this.events.onWordStateChange?.(state);
    }
    /**
     * Called when the user begins pressing/holding (e.g. pointerdown). Only
     * has an effect while armed; marks the current sample offset as the
     * start of the labelled region and begins accepting samples.
     */
    onPressStart() {
        if (this.mode !== "word" || this.wordState !== "armed")
            return;
        if (!this.session)
            return;
        this.pressStartSample = this.session.totalSamples;
        this.pressStartMs =
            (this.pressStartSample / this.session.sampleRate) * 1000;
        this.setWordState("recording");
    }
    /**
     * Called when the user releases the press (e.g. pointerup). Finalizes
     * the region annotation bounding [pressStartSample, currentSample) and
     * transitions to "stopped". The session remains open for inspection
     * until `finishWordCapture` is called.
     */
    async onPressEnd() {
        if (this.mode !== "word" || this.wordState !== "recording")
            return null;
        if (!this.session || this.pressStartMs == null)
            return null;
        const endMs = (this.session.totalSamples / this.session.sampleRate) * 1000;
        const options = this.wordOptions;
        if (!options)
            return null;
        const region = {
            id: `${this.session.id}:${Date.now()}`,
            startMs: this.pressStartMs,
            endMs,
            qualifiedLabel: options.qualifiedLabel,
            percussive: options.percussive ?? false,
            pitchInvariant: options.pitchInvariant ?? false,
            createdAt: Date.now(),
        };
        await upsertRegionAnnotation(this.session.id, region);
        this.session.annotations.push(region);
        this.events.onRegion?.(region);
        this.setWordState("stopped");
        return region;
    }
    /**
     * Finalize the word-capture session: stop ingesting audio, mark the
     * session finalized, and return to idle. Call after `onPressEnd`
     * resolves (or to abandon an armed/countdown session early).
     */
    async finishWordCapture() {
        if (this.mode !== "word")
            return null;
        if (this.wordState !== "stopped") {
            console.warn(`[recording-host] finishWordCapture called in state '${this.wordState}'; ` +
                "a press-release cycle must complete (state 'stopped') before finalizing. " +
                "Use cancelWordCapture() to abandon an in-progress capture instead.");
            return null;
        }
        this.clearCountdown();
        await this.flush();
        await this.settle();
        this.disposeNode();
        if (this.session) {
            await updateSessionStatus(this.session.id, "finalized");
            this.session.status = "finalized";
            this.events.onSessionChange?.(this.session);
            // As with stopFreeRecording, build the envelope pyramid in
            // batch once the word-capture session is finalized, so Label
            // mode can immediately load a waveform for any completed
            // session regardless of which capture mode produced it.
            try {
                const fullPcm = await getFullAudio(this.session.id);
                const pyramid = buildEnvelopePyramid(this.session.id, fullPcm);
                await saveEnvelopePyramid(pyramid);
            }
            catch (err) {
                console.error("[recording-host] failed to build/save envelope pyramid", err);
                this.events.onError?.(err);
            }
        }
        const session = this.session;
        this.session = null;
        this.wordOptions = null;
        this.pressStartSample = null;
        this.pressStartMs = null;
        this.mode = "idle";
        this.setWordState("idle");
        console.info("[recording-host] word capture finished");
        return session;
    }
    /**
     * Abandon an in-progress word capture and discard its session,
     * regardless of `wordState` ("countdown", "armed", "recording", or
     * "stopped"). Use this to cancel out of a capture at any point
     * (e.g. user closes the panel or presses Cancel) as opposed to
     * `finishWordCapture`, which only succeeds once a press-release
     * cycle has completed (`wordState === "stopped"`) and produced a
     * region annotation.
     */
    async cancelWordCapture() {
        if (this.mode !== "word")
            return;
        this.clearCountdown();
        this.disposeNode();
        if (this.session) {
            await deleteSession(this.session.id);
        }
        this.session = null;
        this.wordOptions = null;
        this.pressStartSample = null;
        this.pressStartMs = null;
        this.mode = "idle";
        this.setWordState("idle");
    }
    clearCountdown() {
        if (this.countdownTimer != null) {
            clearTimeout(this.countdownTimer);
            this.countdownTimer = null;
        }
    }
    // ---------------------------------------------------------------------
    // Disposal
    // ---------------------------------------------------------------------
    /** Tear down any active capture and release resources. */
    dispose() {
        this.clearCountdown();
        this.disposeNode();
        for (const w of this.flushWaiters.splice(0))
            w();
        this.paused = false;
        this.session = null;
        this.mode = "idle";
        this.wordState = "idle";
    }
}
